import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  API,
  CompanyDTO,
  FileLink,
  HtmlDTO,
  MarkerDTO,
  MediumFullDTO,
  TemplateStoreDataDTO,
  TemplateStoreDTO,
} from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { EmailEditorComponent } from 'src/app/addons/unlayer/angular-email-editor/email-editor.component';
import { NavigationService, ThemingService } from '@ngx-seatback/utils';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { CompaniesComponent } from '../../company/companies/companies.component';
import { MediumUploadDTO } from '../medium-explorer/medium-explorer.component';
import { HttpErrorResponse } from '@angular/common/http';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { SbBookableMarkersComponent } from 'src/app/components/sb-bookable-markers/sb-bookable-markers.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ImageViewerComponent } from 'src/app/components/controls/image-viewer/image-viewer.component';
import { ContentEditorService } from 'src/app/services/content-editor/content-editor.service';
import { PermissionService } from '../../../services/permission.service';
import { finalize, switchMap, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SbWizardComponent, SbWizardTabComponent } from '@ngx-seatback/layout';

@Component({
  selector: 'sb-page-content-creator',
  templateUrl: './content-creator.component.html',
  styleUrls: ['./content-creator.component.scss'],
  standalone: false,
})
export class ContentCreatorComponent implements OnInit {
  loading: boolean;
  debounceTimer: ReturnType<typeof setTimeout> = null;
  debounceTimerEditorLoad: ReturnType<typeof setTimeout> = null;

  templateId: number;
  templateDataId: number;
  mediumId: number;

  template: TemplateStoreDTO;
  templateData: TemplateStoreDataDTO;
  templateDataTemp: TemplateStoreDataDTO;
  medium: MediumUploadDTO;

  showEditor: boolean = false;
  showPreviewButton: boolean = false;

  form = new UntypedFormGroup({
    Name: new UntypedFormControl('', [Validators.required]),
    Width: new UntypedFormControl(800, [Validators.required]),
    Height: new UntypedFormControl(600, [Validators.required]),
  });

  get Name() {
    return this.form.get('Name');
  }

  get Width() {
    return this.form.get('Width');
  }

  get Height() {
    return this.form.get('Height');
  }

  get editMode(): boolean {
    return !!(this.medium?.id && this.templateData?.id);
  }

  @ViewChild(EmailEditorComponent)
  private emailEditor: EmailEditorComponent;

  projectId: number = 36210;
  displayMode: 'web' | 'email' = 'web';
  locale: string = 'de-DE';
  dockSide: 'left' | 'right' = 'left';
  theme: 'dark' | 'light' = 'light';
  editorPreview: boolean = false;
  stockImages: any = false;

  minHeight = '640px';

  user: string;
  signature: string;

  customer: CompanyDTO;
  loadEditor: boolean = false;

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    public util: UtilService,
    private errorHandler: ErrorService,
    public themingService: ThemingService,
    public translate: TranslateService,
    public modal: ModalService,
    public ngbModal: NgbModal,
    private navService: NavigationService,
    private contentEditorService: ContentEditorService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.templateId = +params.contentEditorID;
      this.mediumId = params.mediumID;
      if (this.templateId || this.mediumId) {
        this.requestDebounced();
      } else {
        this.cancel();
      }
    });
  }

  init() {
    this.themingService.theme.subscribe((theme: string) => {
      switch (theme) {
        case 'dark-theme':
          this.applyTheme('dark');
          break;
        case 'light-theme':
        default:
          this.applyTheme('light');
          break;
      }
    });

    this.applyLocale();
    this.translate.onDefaultLangChange.subscribe(() => {
      this.applyLocale();
    });

    this.api
      .shared_Client_retrieveEditorSignature()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: any) => {
          this.user = res?.id;
          this.signature = res?.signature;
          this.applyOptions();
        },
        error: () => {},
      });

    this.loading = false;
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.load();
    }, 500);
  }

  load() {
    this.loading = true;
    if (this.mediumId) {
      this.api
        .mm_Medium_retrieveById(this.mediumId, { meta: true })
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (result: MediumFullDTO) => {
            this.medium = result as MediumUploadDTO;
            if (!this.medium?.locked && this.medium?.templateStoreData?.id) {
              this.templateData = this.medium.templateStoreData;
              this.templateDataId = this.medium.templateStoreData.id;
              this.Name.setValue(this.medium.name);
              if (this.medium?.resolution?.width && this.medium?.resolution?.height) {
                this.Width.setValue(this.medium.resolution.width);
                this.Height.setValue(this.medium.resolution.height);
              }
              this.customer = this.medium.company as CompanyDTO;
              this.template = this.templateData.templateStore;
              this.init();
            } else {
              this.cancel();
            }
          },
          error: error => {
            this.handleError(error);
            this.cancel();
          },
        });
    } else {
      this.api
        .mm_templating_store_TemplateStore_getTemplateStore(this.templateId)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (res: TemplateStoreDTO) => {
            this.template = res;
            this.init();
          },
          error: error => {
            this.handleError(error);
          },
        });
    }
  }

  cancel() {
    this.util.returnToNavigation('/content/media/content-editor');
  }

  nextStep(wizard: SbWizardComponent, nextTab: SbWizardTabComponent) {
    wizard.selectTab(nextTab);
  }

  submit() {
    this.loading = true;
    this.saveDesign();
  }

  saveDesign() {
    this.emailEditor.editor.saveDesign((data: any) => {
      if (this.templateData) {
        this.templateDataTemp = this.templateData;
      } else {
        this.templateDataTemp = this.api.createDTO('TemplateStoreDataDTO');
      }

      this.templateDataTemp.value = JSON.stringify(data);
      this.templateDataTemp.templateStore = this.template;

      this.exportHtml();
    });
  }

  editorLoaded($event: any) {
    setTimeout(() => {
      if (this.editMode) {
        if (this.templateData?.value) {
          this.emailEditor.loadDesign(JSON.parse(this.templateData.value));
        }
      } else if (this.template?.value) {
        this.emailEditor.loadDesign(JSON.parse(this.template.value));
      }
      this.showPreviewButton = true;
    }, 1500);
  }

  exportHtml() {
    this.emailEditor.editor.exportHtml((data: any) => {
      this.submitToServer(this.contentEditorService.injectCustomCSSToEditorData(data), +this.Width.value, +this.Height.value);
    });
  }

  submitToServer(html: any, width: number, height: number) {
    let medium: MediumFullDTO;
    if (this.editMode) {
      // medium = this.medium;
    } else {
      medium = new MediumFullDTO();
    }
    medium.name = this.Name.value;
    // medium.uploading = true;
    // medium.processing = undefined;
    if (this.customer) {
      medium.company = this.customer;
    } else {
      medium.company = null;
    }

    if (this.editMode) {
      this.uploadMedium(medium, html, width, height);
    } else {
      this.api
        .mm_Medium_create(medium)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: data => {
            medium.id = data.id;
            this.uploadMedium(medium, html, width, height);
          },
          error: error => {
            this.handleError(error);
          },
        });
    }
  }

  uploadMedium(medium: MediumFullDTO, html: any, width: number, height: number) {
    this.templateDataTemp.name = this.Name.value;

    medium.templateStoreData = this.templateDataTemp;

    const htmlDTO: HtmlDTO = this.contentEditorService.createHtmlDTO(html?.html ? html.html : '', width, height, this.templateDataTemp);

    this.uploadMediumImpl(medium, htmlDTO);
  }

  preview() {
    this.loading = true;

    this.emailEditor.editor.saveDesign((data: any) => {
      if (this.templateData) {
        this.templateDataTemp = this.templateData;
      } else {
        this.templateDataTemp = this.api.createDTO('TemplateStoreDataDTO');
      }

      this.templateDataTemp.value = JSON.stringify(data);
      this.templateDataTemp.templateStore = this.template;

      this.emailEditor.editor.exportHtml((htmlData: any) => {
        this.templateDataTemp.name = this.Name.value;

        const htmlDTO: HtmlDTO = this.contentEditorService.createHtmlDTO(
          htmlData?.html ? this.contentEditorService.injectCustomCSSToEditorData(htmlData).html : '',
          +this.Width.value,
          +this.Height.value,
          this.templateDataTemp
        );

        this.api
          .mm_Medium_uploadFileHtmlPreview(htmlDTO)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (result: FileLink) => {
              if (result) {
                const modal = this.ngbModal.open(ImageViewerComponent, { size: 'lg' });
                (modal.componentInstance as ImageViewerComponent).title = this.translate.instant('lang.globals.preview');
                if (this.Name?.value) {
                  (modal.componentInstance as ImageViewerComponent).title +=
                    ' ' + this.translate.instant('lang.globals.medium') + ' ' + this.Name.value;
                }
                (modal.componentInstance as ImageViewerComponent).title += ' ' + '(' + htmlDTO.width + 'x' + htmlDTO.height + ' px' + ')';
                (modal.componentInstance as ImageViewerComponent).src = result.data ? result.data : result;
                (modal.componentInstance as ImageViewerComponent).modalRef = modal;
              }
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      });
    });
  }

  uploadMediumImpl(medium: MediumFullDTO, htmlDTO: HtmlDTO) {
    this.api
      .mm_Medium_edit(medium)
      .pipe(
        switchMap(() => this.api.mm_Medium_uploadFileHtml(medium.id, htmlDTO)),
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (mediumUploadData: MediumUploadDTO) => {
          // medium.processing = true;
          // medium.uploading = undefined;
          this.checkForFileReady(mediumUploadData);
        },
        error: (err: HttpErrorResponse) => {
          // medium.uploading = undefined;
          this.handleError(err);
        },
      });
  }

  checkForFileReady(medium: any) {
    if (medium.processing) {
      setTimeout(() => {
        this.api
          .mm_Medium_retrieveById(medium.id, { meta: true })
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: data => {
              this.checkForFileReady(data);
            },
            error: error => {
              this.handleError(error);
            },
          });
      }, 2500);
    } else {
      this.navigateToMedium(medium.id);
    }
  }

  handleError(error: any) {
    this.errorHandler.errorHandler(error);
    this.loading = false;
  }

  navigateToMedium(id: number) {
    const extras = {
      queryParams: {
        mediumID: id,
      },
    };
    this.navService.navigateToPoint(RoutingID.mediumExplorer, null, extras);
  }

  selectCompany() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: CompaniesComponent, data: {} },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: CompanyDTO) => {
        if (result) {
          this.customer = result;
        } else {
          this.customer = null;
        }
      })
      .catch(() => {});
  }

  applyLocale() {
    if (this.translate.currentLang === 'de') {
      this.locale = 'de-DE';
    } else {
      this.locale = 'en-US';
    }
    this.reloadEditor();
  }

  applyTheme(theme: 'dark' | 'light') {
    this.theme = theme;
    this.reloadEditor();
  }

  applyOptions() {
    this.reloadEditor();
  }

  selectMarker() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: SbBookableMarkersComponent, data: {} },
        title: null,
        modalOptions: {
          // windowClass: 'bigXL',
          size: 'xl',
        },
      })
      .then((result: MarkerDTO) => {
        if (result?.id && result?.resolution) {
          this.Width.setValue(result.resolution.width);
          this.Height.setValue(result.resolution.height);
        }
      })
      .catch(() => {});
  }

  reloadEditor() {
    this.loadEditor = false;

    if (this.showEditor) {
      if (this.debounceTimerEditorLoad) {
        clearTimeout(this.debounceTimerEditorLoad);
      }
      this.debounceTimerEditorLoad = setTimeout(() => {
        this.loadEditor = true;
      }, 500);
    }
  }
}
